import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  CircularProgress,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Box,
  Divider,
  Alert,
  IconButton
} from '@mui/material';

import { feedbackService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Close as CloseIcon } from '@mui/icons-material';
import EmptyState from '../common/EmptyState';
const FeedbackModal = ({ open, onClose, onSubmit, itemId, enrollmentId, instanceId, itemType }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [itemTitle, setItemTitle] = useState('');
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  const { showToast } = useToast();
  
  // Fetch feedback questions when modal opens
  useEffect(() => {
    if (open && itemId && enrollmentId) {
      fetchFeedbackQuestions();
    }
  }, [open, itemId, enrollmentId, itemType]);
  
  // Fetch feedback questions from the server
  const fetchFeedbackQuestions = async () => {
    try {
      setLoading(true);
      const response = await feedbackService.getFeedbackQuestions(itemId, enrollmentId, itemType);
      
      if (response.data) {
        setQuestions(response.data.questions || []);
        setItemTitle(response.data.itemTitle || '');
        
        // Initialize answers object
        const initialAnswers = {};
        (response.data.questions || []).forEach(question => {
          initialAnswers[`q_${question.id}`] = '';
        });
        setAnswers(initialAnswers);
      }
    } catch (error) {
      console.error('Error fetching feedback questions:', error);
      setError('Failed to load feedback questions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [`q_${questionId}`]: value
    }));
  };

  // One answer, to any question, is enough to submit, and needed (S-15.1, agreed 17 Oct); closing the form keeps the
  // request in the learner's notifications. The server checks it too. Removed 17 Oct 2026 (S-15.1, agreed 17 Oct):
  // checking the questions marked required (the first, or all, by the course's or path's setting), with "Please answer
  // all required questions before submitting."
  const validateForm = () => {
    const answered = questions.some(q => String(answers[`q_${q.id}`] || '').trim() !== '');
    
    if (!answered) {
      setError('Please answer at least one question before submitting.');
      return false;
    }
    
    setError('');
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      // Determine which API to call based on item type
      let response;
      if (itemType === 'course') {
        response = await feedbackService.submitFeedback(
          itemId, 
          enrollmentId, 
          instanceId, 
          answers
        );
      } else if (itemType === 'lpath') {
        response = await feedbackService.submitLPathFeedback(
          itemId, 
          enrollmentId, 
          instanceId, 
          answers
        );
      }
      
      if (response.data.success) {
        showToast('Thank you for your feedback!', 'success');
        
        // Call onSubmit to navigate to next content if needed
        if (onSubmit) onSubmit();
        else onClose();
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('An error occurred while submitting your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  // Each question is one of the workspace's, on the agree scale (S-15.1, agreed 17 Oct), with no required mark of its
  // own. Removed 17 Oct 2026 (S-15.1, agreed 17 Oct): the text box for a course's or path's own two questions, which
  // the form no longer asks, the 0 to 10 stars for a mandatory question, which no question now is, and each question's
  // required mark.
  const renderQuestion = (question, index) => {
    const questionId = question.id;
    
    return (
      <Box key={questionId} sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <FormLabel 
            component="legend" 
            sx={{ 
              fontWeight: 'bold', 
              color: 'text.primary',
              mb: 1
            }}
          >
            {index + 1}. {question.question}
            
          </FormLabel>
          
          {/* The agree scale's middle choice is "Neutral" (S-15.1, agreed 17 Oct). Removed 17 Oct 2026 (S-15.1): "OK".
              Past answers keep the word they were given. */}
          <RadioGroup
            value={answers[`q_${questionId}`] || ''}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            row
          >
            {['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'].map((option) => (
              <FormControlLabel 
                key={option} 
                value={option} 
                control={<Radio />} 
                label={option} 
              />
            ))}
          </RadioGroup>
        </FormControl>
        {index < questions.length - 1 && <Divider sx={{ my: 2 }} />}
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      aria-labelledby="feedback-dialog-title"
    >
      <DialogTitle id="feedback-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Feedback</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              {itemTitle}
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography fontWeight="bold">{error}</Typography>
              </Alert>
            )}
            
            {questions.length === 0 ? (
              <>
                {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                <EmptyState framed={false} title="No feedback questions are available at this time." message={null} />
              </>
            ) : (
              <Box component="form" noValidate sx={{ mt: 2 }}>
                {questions.map((question, index) => renderQuestion(question, index))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading || submitting || questions.length === 0}
        >
          {submitting ? <CircularProgress size={24} /> : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;